import { useCallback, useEffect, useRef, useState } from "react";
import { assetUrl } from "../lib/api";
import { useSolo } from "./useVideoStudio";

// Plays trimmed clips back-to-back on one <video>, with looping music kept in step, on the final video's timeline.
export const useSequencePlayer = (segments, music) => {
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);
  const st = useRef({ idx: 0, t: 0, playing: false, pending: null });
  st.current.segs = segments;
  const last = segments[segments.length - 1];
  const total = last ? last.offset + last.len : 0;

  const setPlay = (p) => { st.current.playing = p; setPlaying(p); };
  const setTime = (x) => { st.current.t = x; setT(x); };
  const claim = useSolo("preview", () => {
    if (!st.current.playing) return;
    videoRef.current?.pause();
    audioRef.current?.pause();
    setPlay(false);
  });
  const audio = (time, play) => {
    const a = audioRef.current;
    if (!a) return;
    if (a.duration) a.currentTime = time % a.duration;
    if (play) a.play().catch(() => {}); else a.pause();
  };
  const go = (i, local, play) => {
    const v = videoRef.current, segs = st.current.segs;
    st.current.idx = i;
    setIdx(i);
    if (!v || v.getAttribute("src") !== assetUrl(segs[i].url)) { st.current.pending = { local, play }; return; }
    v.currentTime = local;
    if (play) v.play().catch(() => setPlay(false)); else v.pause();
  };

  const seek = useCallback((time, play) => {
    const segs = st.current.segs;
    if (!segs.length) return;
    const end = segs[segs.length - 1].offset + segs[segs.length - 1].len;
    const T = Math.max(0, Math.min(time, end));
    let i = segs.findIndex((s) => T < s.offset + s.len);
    if (i < 0) i = segs.length - 1;
    if (play) claim();
    setTime(T);
    setPlay(play);
    audio(T, play);
    go(i, segs[i].s + (T - segs[i].offset), play);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const advance = () => {
    const segs = st.current.segs, i = st.current.idx;
    if (i + 1 < segs.length) {
      audioRef.current?.pause();
      setTime(segs[i + 1].offset);
      go(i + 1, segs[i + 1].s, true);
      return;
    }
    videoRef.current?.pause();
    audioRef.current?.pause();
    setPlay(false);
    setTime(segs[i].offset + segs[i].len);
  };

  useEffect(() => {
    if (!playing) return undefined;
    let raf;
    const tick = () => {
      const v = videoRef.current, sg = st.current.segs[st.current.idx];
      if (v && sg && !st.current.pending) {
        if (v.ended || v.currentTime >= sg.e - 0.04) advance();
        else setTime(sg.offset + Math.max(0, v.currentTime - sg.s));
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing]); // eslint-disable-line react-hooks/exhaustive-deps

  const sig = segments.map((s) => `${s.id}:${s.s}:${s.e}`).join("|");
  useEffect(() => { seek(Math.min(st.current.t, total), false); }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (audioRef.current) audioRef.current.volume = music?.volume ?? 0.8; }, [music?.volume, music?.url]);

  const onLoaded = () => {
    const v = videoRef.current, p = st.current.pending, sg = st.current.segs[st.current.idx];
    st.current.pending = null;
    if (!v || !sg) return;
    v.currentTime = p ? p.local : sg.s + Math.max(0, st.current.t - sg.offset);
    if (p ? p.play : st.current.playing) v.play().catch(() => setPlay(false));
  };
  const resync = () => { if (st.current.playing) audio(st.current.t, true); };
  const toggle = () => {
    if (st.current.playing) { videoRef.current?.pause(); audioRef.current?.pause(); setPlay(false); return; }
    seek(st.current.t >= total - 0.05 ? 0 : st.current.t, true);
  };

  const seg = segments[Math.min(idx, segments.length - 1)];
  return { videoRef, audioRef, seg, t, total, playing, seek, toggle, onLoaded, onPlaying: resync, onSeeked: resync };
};

export default useSequencePlayer;
